const rootStyle = getComputedStyle(document.documentElement);
function getDesignValue(prop) {
    const value = rootStyle.getPropertyValue(prop);
    if (value) {
        return value.trim();
    }
    return null;
}
function getDelimitedValue(prop) {
    const value = getDesignValue(prop);
    if (value) {
        return value.split(",").map((x) => x.trim());
    }
    return null;
}
let swatchNames;
let fgSwatchNames;
let colorNames;
let semanticColorNames;
let themeCssNames;
function getSwatchNames() {
    return swatchNames || (swatchNames = getDelimitedValue("--ct-swatch-names"));
}
function getFgSwatchNames() {
    return fgSwatchNames || (fgSwatchNames = getDelimitedValue("--ct-fg-swatch-names"));
}
function getColorNames() {
    return colorNames || (colorNames = getDelimitedValue("--ct-color-names"));
}
function getSemanticColorNames() {
    return (semanticColorNames || (semanticColorNames = getDelimitedValue("--ct-semantic-color-names")));
}
function getThemeCssNames() {
    return themeCssNames || (themeCssNames = getDelimitedValue("--ct-theme-css-names"));
}

const htmlElement = document.documentElement;
const DEFAULT_CATEGORY_KEY = "__DEFAULT_CATEGORY__";
const currentThemes = {};
function _getCurrentTheme(category) {
    if (category === null) {
        category = DEFAULT_CATEGORY_KEY;
    }
    return currentThemes[category];
}
function _setCurrentTheme(category, theme) {
    if (category === null) {
        category = DEFAULT_CATEGORY_KEY;
    }
    currentThemes[category] = theme;
}
function _addThemeClasses(theme) {
    htmlElement.classList.add(theme.cssName);
    htmlElement.classList.add(getThemeBrightnessCssName(theme));
}
function _removeThemeClasses(theme) {
    htmlElement.classList.remove(theme.cssName);
    htmlElement.classList.remove(getThemeBrightnessCssName(theme));
}
let themes;
function getThemeBrightnessCssName(theme) {
    const categoryText = theme.category ? `${theme.category}-` : "";
    return `theme-${categoryText}brightness-${theme.brightness}`;
}
function getUserPreferredBrightness() {
    // We can only do that if the browser supports matchMedia.
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return "dark";
    }
    return "light";
}
function initializeTheming(theme, category = null) {
    if (!theme) {
        const preferredBrightness = getUserPreferredBrightness();
        theme = getThemes(category).filter((t) => t.brightness == preferredBrightness)[0];
    }
    _addThemeClasses(theme);
    _setCurrentTheme(category, theme);
}
function setTheme(theme) {
    const previousTheme = _getCurrentTheme(theme.category);
    _removeThemeClasses(previousTheme);
    _addThemeClasses(theme);
    _setCurrentTheme(theme.category, theme);
}
function getCurrentTheme(category = null) {
    return _getCurrentTheme(category);
}
function getThemes(category = null) {
    return getAllThemes().filter((t) => t.category === category);
}
function getTheme(themeName, category = null) {
    for (const theme of getAllThemes()) {
        if (theme.category === category && theme.name === themeName) {
            return theme;
        }
    }
    throw new Error(`Didn't find theme named '${themeName}' in category '${category}'.`);
}
function getAllThemes() {
    if (themes) {
        return themes;
    }
    const names = getThemeCssNames();
    return (themes = names.map((cssName) => {
        const category = getDesignValue(`--ct-${cssName}-props-category`);
        const name = getDesignValue(`--ct-${cssName}-props-name`);
        const brightness = getDesignValue(`--ct-${cssName}-props-brightness`);
        return {
            category: category || null,
            name,
            cssName,
            brightness,
        };
    }));
}

class ThemeStorage {
    constructor(_key, _defaultTheme, _category) {
        this._key = _key;
        this._defaultTheme = _defaultTheme;
        this._category = _category;
    }
    initializeTheming() {
        const themeName = this._getThemeNameFromLocalStorage();
        const theme = themeName ? getTheme(themeName) : null;
        initializeTheming(theme, this._category);
    }
    setThemeName(themeName) {
        localStorage.setItem(this._key, themeName);
        setTheme(getTheme(themeName));
    }
    _getThemeNameFromLocalStorage() {
        const theme = localStorage.getItem(this._key);
        return theme || this._defaultTheme;
    }
}

export { ThemeStorage, getAllThemes, getColorNames, getCurrentTheme, getDelimitedValue, getDesignValue, getFgSwatchNames, getSemanticColorNames, getSwatchNames, getTheme, getThemeCssNames, getThemes, initializeTheming, setTheme };
